import { ProjectBriefPanel } from './ProjectBrief';
import { ProjectLicensedAssets } from './ProjectLicensedAssets';
import { ProjectTaskEditor } from './ProjectTaskEditor';
import { useQueryClient } from '@tanstack/react-query';
import { orgID, post, useData, useList, useSession } from '@yingbaba/api';
import { ArrowUpRight, Camera, Check, MessageSquare, Plus, Send, Video } from '@yingbaba/icons';
import type {
  Comment,
  Confirmation,
  Conversation,
  Meeting,
  MemberCandidate,
  Milestone,
  Project,
  ProjectActivity,
  ProjectMember,
  TaskView,
  WorkspaceProject,
} from '@yingbaba/types';
import {
  ActionDialog,
  Avatar,
  Button,
  Empty,
  ErrorNotice,
  Field,
  Notice,
  PageTitle,
  Pagination,
  QueryState,
  Select,
  Status,
  useToast,
} from '@yingbaba/ui';
import { date, datetime, beijingToday } from '@yingbaba/utils';
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { MeetingActionsPanel } from './MeetingActions';
import {
  CreateProjectButton,
  ProjectFilesPanel,
  ProjectInvitations,
  ToolsProjectOverview,
} from './ProjectTools';
interface ProjectView {
  project: Project;
  milestones: Milestone[];
  members: ProjectMember[];
  eligible_members: MemberCandidate[];
  tasks: TaskView[];
  comments: Comment[];
  meetings: Meeting[];
  activities: ProjectActivity[];
  confirmations: Confirmation[];
}
export function Projects() {
  const [params, setParams] = useSearchParams();
  // Search-param setters are not queued like React state setters. Preserve
  // pending updates when a user immediately combines multiple filters.
  const pendingParams = useRef(new URLSearchParams(params));
  useEffect(() => {
    pendingParams.current = new URLSearchParams(params);
  }, [params]);
  const page = Math.min(10000, Math.max(1, Math.floor(Number(params.get('page')) || 1))),
    status = ['active', 'completed', 'cancelled'].includes(params.get('status') || '')
      ? params.get('status')!
      : '',
    query = params.get('q') || '';
  const [search, setSearch] = useState(query);
  useEffect(() => setSearch(query), [query]);
  function filter(key: string, value: string) {
    const next = new URLSearchParams(pendingParams.current);
    value ? next.set(key, value) : next.delete(key);
    next.delete('page');
    pendingParams.current = next;
    setParams(next);
  }
  const sort = ['recent', 'deadline', 'created'].includes(params.get('sort') || '')
    ? params.get('sort')!
    : 'recent';
  const q = useList<WorkspaceProject>(
    `/workspace/projects?page=${page}&q=${encodeURIComponent(query)}&status=${encodeURIComponent(status)}&sort=${sort}`,
  );
  return (
    <>
      <PageTitle
        title="我的项目"
        description="把每个阶段的进展，放在同一个地方。"
        action={<CreateProjectButton />}
      />
      <ProjectInvitations />
      <form
        className="project-list-filter"
        role="search"
        aria-label="查找项目"
        onSubmit={(e) => {
          e.preventDefault();
          filter('q', search.trim());
        }}
      >
        <input
          className="input"
          aria-label="项目关键词"
          value={search}
          maxLength={200}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="搜索项目名称、目标或类型"
        />
        <Select aria-label="项目状态" value={status} onChange={(v) => filter('status', v)}>
          <option value="">全部项目</option>
          <option value="active">进行中</option>
          <option value="completed">已归档</option>
          <option value="cancelled">已关闭</option>
        </Select>
        <Select aria-label="项目排序" value={sort} onChange={(v) => filter('sort', v)}>
          <option value="recent">最近有进展</option>
          <option value="deadline">计划日期优先</option>
          <option value="created">最近创建</option>
        </Select>
        <Button type="submit" variant="secondary">
          搜索
        </Button>
      </form>
      <QueryState query={q}>
        {!q.data?.items.length && (
          <Empty
            title={query || status ? '没有找到相关项目' : '从第一个创作项目开始'}
            description={
              query || status
                ? '试试其他关键词或项目状态。'
                : '从空白项目开始，或选择制作模板，逐步安排团队、任务和素材。'
            }
            action={
              query || status || page > 1 ? (
                <Button variant="secondary" onClick={() => setParams({})}>
                  查看全部项目
                </Button>
              ) : undefined
            }
          />
        )}
        <div className="grid-2 project-catalog-grid">
          {q.data?.items.map((p) => (
            <article className="panel project-catalog-card" key={p.id}>
              <div className="row spread">
                <span className="icon-tile">
                  <Camera size={21} />
                </span>
                <span className="tag">
                  {p.status === 'completed'
                    ? '已归档'
                    : p.status === 'cancelled'
                      ? '已关闭'
                      : '进行中'}
                </span>
              </div>
              <h3>
                <Link to={`/projects/${p.id}`}>{p.title}</Link>
              </h3>
              <p className="small muted">
                {p.category || '创作项目'}
                {p.summary?.current_stage ? ` · ${p.summary.current_stage}` : ''}
              </p>
              {p.description && (
                <p className="project-card-description small muted">{p.description}</p>
              )}
              {p.summary && (
                <div className="project-card-progress">
                  <div className="row spread">
                    <span>任务进展</span>
                    <span>
                      {p.summary.completed_task_count} / {p.summary.task_count}
                    </span>
                  </div>
                  {p.summary.task_count > 0 && (
                    <div
                      className="project-progress-track"
                      role="progressbar"
                      aria-label={`${p.title}任务完成进度`}
                      aria-valuemin={0}
                      aria-valuemax={p.summary.task_count}
                      aria-valuenow={p.summary.completed_task_count}
                    >
                      <span
                        style={{
                          width: `${Math.round((p.summary.completed_task_count / p.summary.task_count) * 100)}%`,
                        }}
                      />
                    </div>
                  )}
                  {p.status === 'active' && p.summary.overdue_task_count > 0 && (
                    <Link
                      className="project-overdue-link"
                      to={`/projects/${p.id}?tab=tasks&task_status=overdue`}
                    >
                      {p.summary.overdue_task_count} 项任务已逾期
                    </Link>
                  )}
                </div>
              )}
              {p.status === 'active' && p.summary?.next_task && (
                <Link
                  className="project-card-next"
                  to={`/projects/${p.id}?tab=tasks&task=${p.summary.next_task.id}`}
                >
                  <span className="small muted">
                    我的下一项任务
                    {p.summary.my_pending_tasks > 1
                      ? ` · 共 ${p.summary.my_pending_tasks} 项待办`
                      : ''}
                  </span>
                  <strong>{p.summary.next_task.title}</strong>
                </Link>
              )}
              <div className="project-card-footer">
                <div>
                  <span>{p.deadline ? `计划 ${date(p.deadline)} 完成` : '未设置完成日期'}</span>
                  {p.summary?.last_activity_at && (
                    <small>最近更新 {datetime(p.summary.last_activity_at)}</small>
                  )}
                </div>
                <Link className="link" to={`/projects/${p.id}`}>
                  进入项目
                  <ArrowUpRight size={16} />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </QueryState>
      <Pagination
        page={page}
        size={q.data?.page_size || 30}
        total={q.data?.total || 0}
        onPage={(page) =>
          setParams((p) => {
            p.set('page', String(page));
            return p;
          })
        }
      />
    </>
  );
}

function projectTabKeys(event: KeyboardEvent<HTMLDivElement>) {
  if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
  const buttons = Array.from(
    event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]'),
  );
  const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
  if (index < 0) return;
  event.preventDefault();
  const next =
    event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? buttons.length - 1
        : (index + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length;
  buttons[next].focus();
  buttons[next].click();
}

const groups = [
  { key: 'overview', label: '项目概览', tabs: [['overview', '项目概览']] },
  { key: 'brief', label: '创作需求', tabs: [['brief', '创作需求']] },
  {
    key: 'collaboration',
    label: '协作',
    tabs: [
      ['team', '项目团队'],
      ['tasks', '任务与确认'],
      ['activity', '项目动态'],
      ['comments', '沟通记录'],
      ['meetings', '会议'],
    ],
  },
  { key: 'files', label: '项目素材', tabs: [['files', '项目文件']] },
];
export function ProjectDetail() {
  const { id } = useParams(),
    q = useData<ProjectView>('/projects/' + id),
    session = useSession(),
    navigate = useNavigate(),
    qc = useQueryClient(),
    toast = useToast();
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab') || 'overview';
  const tab = groups.some((g) => g.tabs.some(([key]) => key === raw)) ? raw : 'overview';
  const group = groups.find((g) => g.tabs.some(([key]) => key === tab))!;
  useEffect(() => {
    if (raw !== tab) setParams({ tab }, { replace: true });
  }, [raw, tab, setParams]);
  const [error, setError] = useState<unknown>(null);
  const data = q.data,
    role = session.data?.organizations.find((o) => o.id === orgID())?.role || '';
  const taskOwner = params.get('assignee') || '',
    taskStatus = params.get('task_status') || '';
  const taskStage = params.get('stage') || '',
    taskQuery = params.get('task_q') || '';
  const [taskSearch, setTaskSearch] = useState(taskQuery);
  useEffect(() => setTaskSearch(taskQuery), [taskQuery]);
  const today = beijingToday();
  const visibleTasks = (data?.tasks || []).filter(
    (t) =>
      (!taskStage || t.milestone_id === taskStage) &&
      taskQuery
        .trim()
        .toLowerCase()
        .split(/\s+/)
        .every((term) =>
          `${t.title} ${t.description || ''} ${t.assignee || ''}`.toLowerCase().includes(term),
        ) &&
      (!taskOwner ||
        (taskOwner === 'me'
          ? t.assignee_account_id === session.data?.account.id
          : taskOwner === 'unassigned'
            ? !t.assignee_account_id
            : t.assignee_account_id === taskOwner)) &&
      (!taskStatus ||
        (taskStatus === 'completed'
          ? t.status === 'completed'
          : taskStatus === 'overdue'
            ? data?.project.status === 'active' &&
              t.status !== 'completed' &&
              !!t.due_date &&
              t.due_date < today
            : t.status !== 'completed')),
  );
  function filterTasks(name: string, value: string) {
    setParams((p) => {
      value ? p.set(name, value) : p.delete(name);
      p.delete('task');
      return p;
    });
  }
  const focusedTask = params.get('task') || '';
  const taskFocusKey = useRef('');
  useEffect(() => {
    if (tab !== 'tasks' || !focusedTask || !data) {
      taskFocusKey.current = '';
      return;
    }
    const key = `${id}:${focusedTask}:${taskQuery}:${taskStage}:${taskOwner}:${taskStatus}`;
    if (taskFocusKey.current === key) return;
    const frame = requestAnimationFrame(() => {
      const card = document.getElementById(`project-task-${focusedTask}`);
      if (card) taskFocusKey.current = key;
      card?.scrollIntoView({ block: 'center' });
      card?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [tab, focusedTask, data, id, taskQuery, taskStage, taskOwner, taskStatus]);
  const closed = !!data && ['completed', 'cancelled'].includes(data.project.status);
  const manage = ['owner', 'admin', 'manager'].includes(role),
    write = !closed && ['owner', 'admin', 'manager', 'member'].includes(role),
    client = data?.project.client_id === orgID();
  const memberOptions = [
    { value: '', label: '未指定负责人' },
    ...(data?.members || []).map((member) => ({
      value: member.account_id,
      label: `${member.name} · ${member.role === 'project_manager' ? '项目经理' : '协作者'}`,
    })),
  ];
  const milestoneOptions = [
    { value: '', label: '不关联里程碑' },
    ...(data?.milestones || []).map((milestone) => ({
      value: milestone.id,
      label: milestone.title,
    })),
  ];
  const actionLocks = useRef(new Set<string>());
  const [pendingActions, setPendingActions] = useState<string[]>([]);
  function pendingAction(path: string) {
    return pendingActions.includes(path);
  }
  async function action(path: string, payload: unknown = {}) {
    if (actionLocks.current.has(path)) return false;
    actionLocks.current.add(path);
    setPendingActions([...actionLocks.current]);
    setError(null);
    try {
      await post(path, payload);
      await qc.invalidateQueries();
      toast('已保存');
      return true;
    } catch (e) {
      setError(e);
      return false;
    } finally {
      actionLocks.current.delete(path);
      setPendingActions([...actionLocks.current]);
    }
  }
  const chatLock = useRef(false);
  const [openingChat, setOpeningChat] = useState(false);
  async function openProjectConversation() {
    if (chatLock.current) return;
    chatLock.current = true;
    setOpeningChat(true);
    setError(null);
    try {
      const conversation = await post<Conversation>(`/projects/${id}/conversation`, {});
      navigate(`/messages?conversation=${conversation.id}`);
    } catch (e) {
      setError(e);
    } finally {
      chatLock.current = false;
      setOpeningChat(false);
    }
  }
  return (
    <QueryState query={q}>
      {data && (
        <div className="project-detail-page">
          <div className="breadcrumb" style={{ marginTop: 0 }}>
            <Link to="/projects">我的项目</Link>
            <span>/</span>
            <span>项目协作</span>
          </div>
          <PageTitle
            title={data.project.title}
            description={`${data.project.category || '创作项目'} · 项目 ${data.project.id.slice(-8)}`}
            action={
              <div className="row wrap project-header-actions">
                <Button
                  variant="secondary"
                  loading={openingChat}
                  onClick={() => void openProjectConversation()}
                >
                  <MessageSquare size={16} />
                  项目沟通
                </Button>
                <span className="tag">
                  {data.project.status === 'completed'
                    ? '已归档'
                    : data.project.status === 'cancelled'
                      ? '已关闭'
                      : '进行中'}
                </span>
              </div>
            }
          />
          <div
            className="tabs project-primary-tabs"
            role="tablist"
            aria-label="项目功能"
            onKeyDown={projectTabKeys}
          >
            {groups.map((g) => (
              <button
                type="button"
                role="tab"
                aria-selected={group.key === g.key}
                tabIndex={group.key === g.key ? 0 : -1}
                aria-controls="project-content"
                className={group.key === g.key ? 'active' : ''}
                key={g.key}
                onClick={() => {
                  if (group.key !== g.key) setParams({ tab: g.tabs[0][0] });
                }}
              >
                {g.label}
              </button>
            ))}
          </div>
          {group.key === 'collaboration' && (
            <div
              className="tabs project-subtabs"
              role="tablist"
              aria-label="协作功能"
              onKeyDown={projectTabKeys}
            >
              {group.tabs.map(([key, label]) => (
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === key}
                  tabIndex={tab === key ? 0 : -1}
                  aria-controls="project-content"
                  className={tab === key ? 'active' : ''}
                  key={key}
                  onClick={() => setParams({ tab: key })}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          {closed && (
            <Notice>
              项目{data.project.status === 'completed' ? '已归档' : '已关闭'}，现有资料仍可查阅。
              {client && manage
                ? '需要继续协作时，可在项目概览重新打开。'
                : '需要继续协作时，请联系发起方管理员重新打开。'}
            </Notice>
          )}
          <ErrorNotice error={error} />
          <div
            id="project-content"
            className="project-tab-panel"
            role="tabpanel"
            aria-label={group.tabs.find(([key]) => key === tab)?.[1]}
          >
            {tab === 'overview' && (
              <ToolsProjectOverview
                project={data.project}
                milestones={data.milestones}
                tasks={data.tasks}
                activities={data.activities}
                manage={manage}
                confirmations={data.confirmations}
              />
            )}
            {tab === 'brief' && (
              <ProjectBriefPanel
                key={`${orgID()}:${data.project.id}`}
                project={data.project}
                write={manage && !!client && !closed}
              />
            )}
            {tab === 'team' && (
              <div className="grid-2">
                <section className="panel">
                  <div className="panel-title">
                    <div>
                      <h3>项目成员</h3>
                      <p className="small muted" style={{ marginTop: 7 }}>
                        每个协作组织分别维护自己的项目成员。
                      </p>
                    </div>
                    {manage && !closed && data.eligible_members.length > 0 && (
                      <ActionDialog
                        title="添加项目成员"
                        trigger={
                          <>
                            <Plus size={14} />
                            添加成员
                          </>
                        }
                        path={`/projects/${id}/members`}
                        fields={[
                          {
                            name: 'account_id',
                            label: '组织成员',
                            options: data.eligible_members.map((member) => ({
                              value: member.account_id,
                              label: `${member.name} · ${member.email}`,
                            })),
                          },
                          {
                            name: 'role',
                            label: '项目角色',
                            options: [
                              { value: 'project_manager', label: '项目经理' },
                              { value: 'contributor', label: '协作者' },
                            ],
                          },
                        ]}
                      />
                    )}
                  </div>
                  {data.members.length ? (
                    data.members.map((member) => (
                      <div className="list-item" key={member.id}>
                        <Avatar name={member.name} src={member.avatar_url} />
                        <div className="grow">
                          <strong>{member.name}</strong>
                          <p>
                            {member.organization_id === data.project.client_id
                              ? '发起组织'
                              : '协作组织'}{' '}
                            · {member.role === 'project_manager' ? '项目经理' : '协作者'}
                          </p>
                        </div>
                        {manage &&
                          !closed &&
                          member.organization_id === orgID() &&
                          member.account_id !== session.data?.account.id && (
                            <ActionDialog
                              title="移除项目成员"
                              intent="destructive"
                              submitLabel="确认移除成员"
                              trigger="移除"
                              variant="secondary"
                              path={`/project-members/${member.id}/remove`}
                              description={`移除 ${member.name} 后，其未完成任务会自动取消负责人。`}
                            />
                          )}
                      </div>
                    ))
                  ) : (
                    <Empty
                      title="还没有项目成员"
                      description="先把实际参与项目的同事加入进来，再分配任务和会议行动项。"
                    />
                  )}
                </section>
                <section className="panel">
                  <h3>当前组织可加入成员</h3>
                  <p className="muted small" style={{ marginTop: 8 }}>
                    这里只显示当前组织尚未加入本项目的成员。
                  </p>
                  <hr className="divider" />
                  {data.eligible_members.length ? (
                    data.eligible_members.map((member) => (
                      <div className="list-item" key={member.account_id}>
                        <div className="grow">
                          <strong>{member.name}</strong>
                          <p>
                            {member.email} ·{' '}
                            {{
                              owner: '组织所有者',
                              admin: '组织管理员',
                              manager: '项目负责人',
                              member: '组织成员',
                              finance: '财务成员',
                            }[member.organization_role] || '组织成员'}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <Notice>当前组织成员都已加入项目，或暂无其他可加入成员。</Notice>
                  )}
                </section>
              </div>
            )}
            {tab === 'tasks' && (
              <div className="project-collaboration-grid">
                <section className="panel">
                  <div className="panel-title">
                    <h3>制作任务</h3>
                    {write && (
                      <ProjectTaskEditor
                        projectID={id!}
                        members={data.members}
                        milestones={data.milestones}
                        onSaved={(taskID) =>
                          setParams(
                            (p) => {
                              p.set('task', taskID);
                              return p;
                            },
                            { replace: true },
                          )
                        }
                      />
                    )}
                  </div>
                  <form
                    className="project-task-search"
                    role="search"
                    aria-label="查找项目任务"
                    onSubmit={(e) => {
                      e.preventDefault();
                      filterTasks('task_q', taskSearch.trim());
                    }}
                  >
                    <input
                      className="input"
                      aria-label="任务关键词"
                      placeholder="搜索任务、说明或负责人"
                      maxLength={200}
                      value={taskSearch}
                      onChange={(e) => setTaskSearch(e.target.value)}
                    />
                    <Button type="submit" variant="secondary">
                      搜索任务
                    </Button>
                  </form>
                  <div className="project-task-filters">
                    <Select
                      className="project-task-stage-filter"
                      aria-label="筛选任务阶段"
                      value={taskStage}
                      onChange={(v) => filterTasks('stage', v)}
                      options={[
                        { value: '', label: '全部阶段' },
                        ...milestoneOptions.filter((v) => v.value),
                      ]}
                    />
                    <Select
                      aria-label="筛选任务负责人"
                      value={taskOwner}
                      onChange={(v) => filterTasks('assignee', v)}
                      options={[
                        { value: '', label: '全部负责人' },
                        { value: 'me', label: '分配给我' },
                        { value: 'unassigned', label: '未指定负责人' },
                        ...(data.members || [])
                          .filter((m) => m.account_id !== session.data?.account.id)
                          .map((m) => ({ value: m.account_id, label: m.name })),
                      ]}
                    />
                    <Select
                      aria-label="筛选任务状态"
                      value={taskStatus}
                      onChange={(v) => filterTasks('task_status', v)}
                      options={[
                        { value: '', label: '全部状态' },
                        { value: 'pending', label: '待处理' },
                        { value: 'overdue', label: '已逾期' },
                        { value: 'completed', label: '已完成' },
                      ]}
                    />
                  </div>
                  {focusedTask &&
                    data.tasks.some((task) => task.id === focusedTask) &&
                    !visibleTasks.some((task) => task.id === focusedTask) && (
                      <Notice>
                        这项任务不在当前筛选结果中。
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setParams({ tab: 'tasks', task: focusedTask })}
                        >
                          查看这项任务
                        </Button>
                      </Notice>
                    )}
                  {visibleTasks.length ? (
                    visibleTasks.map((t) => (
                      <div
                        className={`panel project-task-card${focusedTask === t.id ? ' is-target' : ''}`}
                        id={`project-task-${t.id}`}
                        tabIndex={-1}
                        aria-label={`任务：${t.title}`}
                        key={t.id}
                        style={{ marginTop: 12 }}
                      >
                        <div className="row spread">
                          <div className="row">
                            <button
                              aria-label={`${t.status === 'completed' ? '重新打开' : '完成'}任务：${t.title}`}
                              disabled={
                                !write ||
                                pendingAction(
                                  `/collaboration/tasks/${t.id}/${t.status === 'completed' ? 'reopen' : 'complete'}`,
                                )
                              }
                              aria-busy={pendingAction(
                                `/collaboration/tasks/${t.id}/${t.status === 'completed' ? 'reopen' : 'complete'}`,
                              )}
                              className={`task-check ${t.status === 'completed' ? 'done' : ''}`}
                              onClick={() =>
                                action(
                                  `/collaboration/tasks/${t.id}/${t.status === 'completed' ? 'reopen' : 'complete'}`,
                                )
                              }
                            >
                              {t.status === 'completed' && <Check size={14} />}
                            </button>
                            <div>
                              <strong className={t.status === 'completed' ? 'task-done' : ''}>
                                {t.title}
                              </strong>
                              <p className="small muted">
                                {t.assignee || '未指定负责人'} ·{' '}
                                {t.priority === 'urgent'
                                  ? '紧急'
                                  : t.priority === 'high'
                                    ? '高优先级'
                                    : t.priority === 'low'
                                      ? '低优先级'
                                      : '普通优先级'}
                                {t.due_date ? ` · ${date(t.due_date)} 截止` : ''}
                              </p>
                            </div>
                          </div>
                          <Status value={t.status} />
                        </div>
                        {t.description && (
                          <p className="prose" style={{ marginTop: 12 }}>
                            {t.description}
                          </p>
                        )}
                        {t.checklist.length > 0 && (
                          <div className="stack" style={{ marginTop: 14 }}>
                            {t.checklist.map((item) => (
                              <div className="row" key={item.id}>
                                <button
                                  type="button"
                                  className={`task-check ${item.completed ? 'done' : ''}`}
                                  disabled={
                                    !write || pendingAction(`/task-checklists/${item.id}/toggle`)
                                  }
                                  aria-busy={pendingAction(`/task-checklists/${item.id}/toggle`)}
                                  aria-label={`${item.completed ? '取消完成' : '完成'}检查项：${item.content}`}
                                  onClick={() =>
                                    action(`/task-checklists/${item.id}/toggle`, {
                                      completed: !item.completed,
                                    })
                                  }
                                >
                                  {item.completed && <Check size={13} />}
                                </button>
                                <span className={item.completed ? 'task-done small' : 'small'}>
                                  {item.content}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        {write && t.status !== 'completed' && (
                          <div className="row wrap project-task-card-actions">
                            <ProjectTaskEditor
                              key={`${t.id}:${orgID()}:${session.data?.account.id}`}
                              projectID={id!}
                              task={t}
                              members={data.members}
                              milestones={data.milestones}
                              onSaved={(taskID) =>
                                setParams(
                                  (p) => {
                                    p.set('task', taskID);
                                    return p;
                                  },
                                  { replace: true },
                                )
                              }
                            />
                            <ActionDialog
                              title="新增任务检查项"
                              trigger="添加检查项"
                              variant="secondary"
                              path={`/tasks/${t.id}/checklist`}
                              submitLabel="添加检查项"
                              fields={[{ name: 'content', label: '检查项内容', min: 2 }]}
                            />
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <Empty
                      title={data.tasks.length ? '没有匹配的任务' : '还没有制作任务'}
                      description={
                        data.tasks.length
                          ? '调整关键词、阶段、负责人或状态，查看其他任务。'
                          : '把待办分解成清楚的每一步，并分配给真实项目成员。'
                      }
                      action={
                        taskQuery || taskStage || taskOwner || taskStatus ? (
                          <Button variant="secondary" onClick={() => setParams({ tab: 'tasks' })}>
                            查看全部任务
                          </Button>
                        ) : undefined
                      }
                    />
                  )}
                </section>
                <section className="panel">
                  <div className="panel-title">
                    <h3>需求确认清单</h3>
                    {write && (
                      <ActionDialog
                        title="添加确认事项"
                        trigger="添加事项"
                        path={`/projects/${id}/confirmations`}
                        fields={[
                          { name: 'content', label: '需要确认的内容', type: 'textarea', min: 2 },
                        ]}
                      />
                    )}
                  </div>
                  {data.confirmations.length ? (
                    data.confirmations.map((v) => (
                      <div className="list-item" key={v.id}>
                        <div className="grow">
                          <p style={{ color: 'var(--ink)' }}>{v.content}</p>
                          {v.confirmed_by && (
                            <small className="muted">由 {v.confirmed_by} 确认</small>
                          )}
                        </div>
                        {client && manage && !closed && v.status === 'pending' ? (
                          <ActionDialog
                            title="确认项目事项"
                            trigger="确认"
                            path={`/collaboration/confirmations/${v.id}/confirm`}
                            description={v.content}
                          />
                        ) : (
                          <Status value={v.status} />
                        )}
                      </div>
                    ))
                  ) : (
                    <Empty
                      title="先把重要的事情对齐"
                      description="脚本方向、拍摄要求、交付规格都可以加入清单。"
                    />
                  )}
                </section>
              </div>
            )}
            {tab === 'activity' && (
              <section className="panel">
                <div className="panel-title">
                  <div>
                    <h3>项目动态</h3>
                    <p className="small muted" style={{ marginTop: 7 }}>
                      团队、任务、会议、确认事项和文件操作会保存在协作时间线中。
                    </p>
                  </div>
                  <span className="small muted">最近 {data.activities.length} 条</span>
                </div>
                {data.activities.length ? (
                  <div className="timeline">
                    {data.activities.map((item) => (
                      <div className="timeline-item" key={item.id}>
                        <div className="row spread">
                          <strong>{item.title}</strong>
                          <small>{datetime(item.created_at)}</small>
                        </div>
                        {item.content && <p className="prose">{item.content}</p>}
                        <small>{item.actor_name || '系统'}</small>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Empty
                    title="还没有项目动态"
                    description="从添加项目成员或创建第一个任务开始，关键协作进展会记录在这里。"
                  />
                )}
              </section>
            )}
            {tab === 'comments' && (
              <section className="panel">
                <h3>项目沟通记录</h3>
                {data.comments.length ? (
                  <div className="timeline">
                    {data.comments
                      .slice()
                      .reverse()
                      .map((c) => (
                        <div className="timeline-item" key={c.id}>
                          <div className="row spread">
                            <strong>{c.author}</strong>
                            <small>{datetime(c.created_at)}</small>
                          </div>
                          <p className="prose">{c.content}</p>
                        </div>
                      ))}
                  </div>
                ) : (
                  <Empty
                    title="说说这次创作的想法"
                    description="与项目有关的讨论会持久保存在这里。"
                  />
                )}
                {write && (
                  <form
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const form = e.currentTarget;
                      const content = String(new FormData(form).get('content') || '').trim();
                      if (!content) {
                        setError(new Error('请填写沟通记录后再保存。'));
                        return;
                      }
                      if (await action(`/projects/${id}/comments`, { content })) form.reset();
                    }}
                    className="form-stack"
                  >
                    <Field label="新的沟通记录" htmlFor="comment">
                      <textarea
                        id="comment"
                        name="content"
                        disabled={pendingAction(`/projects/${id}/comments`)}
                        placeholder="记录一个想法、一条反馈或一次重要决定…"
                        required
                        maxLength={4000}
                      />
                    </Field>
                    <div className="row" style={{ justifyContent: 'flex-end' }}>
                      <Button type="submit" loading={pendingAction(`/projects/${id}/comments`)}>
                        <Send size={16} />
                        保存沟通记录
                      </Button>
                    </div>
                  </form>
                )}
              </section>
            )}
            {tab === 'meetings' && (
              <>
                <div className="project-meeting-toolbar">
                  <p className="muted">会议在腾讯会议或飞书进行，议程与纪要保存在项目中。</p>
                  {write && (
                    <ActionDialog
                      title="预约项目会议"
                      submitLabel="预约会议"
                      trigger={
                        <>
                          <Plus size={15} />
                          预约会议
                        </>
                      }
                      path={`/projects/${id}/meetings`}
                      fields={[
                        { name: 'title', label: '会议主题' },
                        {
                          name: 'provider',
                          label: '会议平台',
                          options: [
                            { value: 'tencent', label: '腾讯会议' },
                            { value: 'feishu', label: '飞书会议' },
                          ],
                        },
                        {
                          name: 'join_url',
                          label: '会议链接',
                          type: 'url',
                          hint: '请粘贴官方 HTTPS 会议链接。',
                        },
                        { name: 'starts_at', label: '开始时间', type: 'datetime-local' },
                        { name: 'agenda', label: '会议议程', type: 'textarea', required: false },
                      ]}
                      transform={(v) => ({ ...v, starts_at: new Date(v.starts_at).toISOString() })}
                    />
                  )}
                </div>
                <div className="grid-2 project-meetings-grid">
                  {data.meetings.length ? (
                    [...data.meetings]
                      .sort((a, b) => {
                        const x = Date.parse(a.starts_at),
                          y = Date.parse(b.starts_at),
                          now = Date.now();
                        return (
                          (x >= now ? 0 : 1) - (y >= now ? 0 : 1) || (x >= now ? x - y : y - x)
                        );
                      })
                      .map((m) => (
                        <article className="meeting-card" key={m.id}>
                          <div className="row spread wrap">
                            <span className="tag">
                              <Video size={13} />
                              {m.provider === 'tencent' ? '腾讯会议' : '飞书会议'}
                            </span>
                            <span className="small muted">{datetime(m.starts_at)}</span>
                          </div>
                          <h3 style={{ marginTop: 20 }}>{m.title}</h3>
                          <p>{m.agenda || '暂无议程'}</p>
                          <div className="row wrap">
                            <a
                              href={m.join_url}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-primary btn-sm"
                            >
                              进入会议
                              <ArrowUpRight size={15} />
                            </a>
                            {write && (
                              <ActionDialog
                                title="会议行动项转任务"
                                trigger="转为任务"
                                variant="secondary"
                                path={`/meetings/${m.id}/action-items`}
                                fields={[
                                  { name: 'title', label: '行动任务' },
                                  {
                                    name: 'assignee_account_id',
                                    label: '负责人',
                                    options: memberOptions,
                                    required: false,
                                  },
                                  {
                                    name: 'due_date',
                                    label: '截止日期',
                                    type: 'date',
                                    required: false,
                                  },
                                ]}
                              />
                            )}
                            {manage && !closed && !m.confirmed && (
                              <ActionDialog
                                title="编辑会议纪要"
                                trigger="记录纪要"
                                variant="secondary"
                                path={`/collaboration/meetings/${m.id}/minutes`}
                                fields={[
                                  {
                                    name: 'content',
                                    label: '会议结论与后续待办',
                                    type: 'textarea',
                                    value: m.minutes,
                                    min: 2,
                                  },
                                ]}
                              />
                            )}
                          </div>
                          {m.minutes && (
                            <MeetingActionsPanel
                              meeting={m}
                              members={memberOptions}
                              write={write}
                            />
                          )}
                          {m.minutes && (
                            <>
                              <hr className="divider" />
                              <p className="prose">{m.minutes}</p>
                              {m.confirmed ? (
                                <Status value="confirmed" />
                              ) : (
                                manage &&
                                !closed && (
                                  <ActionDialog
                                    title="确认会议纪要"
                                    trigger="确认纪要"
                                    path={`/collaboration/meetings/${m.id}/confirm`}
                                    description="确认后的会议纪要将保留为正式记录。"
                                  />
                                )
                              )}
                            </>
                          )}
                        </article>
                      ))
                  ) : (
                    <Empty
                      title="把重要讨论安排起来"
                      description="预约会议并记录议程，让沟通更有准备。"
                    />
                  )}
                </div>
              </>
            )}
            {tab === 'files' && (
              <>
                <ProjectFilesPanel key={id} projectID={id!} write={write} />
                <ProjectLicensedAssets
                  key={`licensed:${id}`}
                  projectID={id!}
                  ownerID={data.project.client_id}
                  write={write}
                />
              </>
            )}
          </div>
        </div>
      )}
    </QueryState>
  );
}
